import React, {useCallback, useReducer, useState} from 'react';
import produce from 'immer';
import {createMesh, type MeshType} from 'api/generated';
import {useCreateFile} from 'hooks';
import {ModelItem, Button, InputFile, Modal, ModelViewer} from 'components';

interface IModalModelAddProps {
  visible: boolean;
  onUploaded: (mesh: MeshType) => void;
  onClose: () => void;
  onConfirm: (meshes: MeshType[]) => void;
  addedMeshes: MeshType[];
  meshes: MeshType[];
  projectUid?: string;
}

const ModalModelAdd = ({visible, onClose, onConfirm, onUploaded, addedMeshes, meshes, projectUid}: IModalModelAddProps) => {
  const [showModel, toggleModel] = useReducer(s => !s, false);
  const [toShow, setToShow] = useState<MeshType>();
  const [toAdd, setToAdd] = useState<MeshType[]>([]);

  const {handleUpload, inputRef, isPending, progress} = useCreateFile({
    createFile: createMesh,
    callback: onUploaded,
    projectUid,
  });

  const handleClicked = useCallback((mesh: MeshType) => {
    setToAdd(
      produce(draft => {
        const index = draft.findIndex(m => m.uid === mesh.uid);
        if (index === -1) {
          draft.unshift(mesh);
        } else {
          draft.splice(index, 1);
        }
      }),
    );
  }, []);

  const handleShow = useCallback((mesh: MeshType) => {
    setToShow(mesh);
    toggleModel();
  }, []);

  const handleConfirm = async () => {
    onConfirm(toAdd);
    await new Promise(resolve => setTimeout(resolve, 500));
    setToAdd([]);
  };

  return (
    <>
      <Modal visible={visible} onClose={onClose} backdropClose={!isPending} size="large" fullHeight>
        <div className="flex flex-col h-full">
          <InputFile
            inputRef={inputRef}
            buttonText="+ Add model file"
            accept="model/gltf-binary"
            supportedFormats={['glb', 'gltf']}
            onUpload={handleUpload}
            loading={isPending}
            progress={progress}
          />
          <div className="flex flex-col flex-1 justify-between">
            <ul className="mt-2 grid grid-cols-2 sm:grid-cols-6 gap-y-1">
              {meshes.map(mesh => {
                const checked = !!toAdd.find(s => s.uid === mesh.uid);
                const disabled = !!addedMeshes.find(s => s.uid === mesh.uid);
                return (
                  <ModelItem key={mesh.uid} mesh={mesh} readOnly onClick={handleClicked} onShow={handleShow} checked={checked} disabled={disabled} />
                );
              })}
            </ul>
            <div className="flex justify-between items-center">
              <Button text="Cancel" variant="secondary" size="extra-small" onClick={onClose} disabled={isPending} />
              <Button text="Confirm" size="extra-small" onClick={handleConfirm} disabled={isPending || toAdd.length === 0} />
            </div>
          </div>
        </div>
      </Modal>
      <Modal visible={showModel} onClose={toggleModel}>
        {!!toShow && <ModelViewer mesh={toShow} />}
      </Modal>
    </>
  );
};

export default React.memo(ModalModelAdd);
